import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { hasValue, roundOff, toBool, toNum, toStr } from "@utils";
import { JSONPath } from 'jsonpath-plus';
import FormField from '@widget/forms/inputs/FormField';
import Label from '@widget/forms/inputs/Label';
import Input from '@widget/forms/inputs/Input';
/**
 * @export
 * Available format options for pills
 */
export const FormatOptions = [
    {
        id: 'string',
        label: 'Text',
        dispalyLabel: 'ABC',
        formatValue: (val) => toStr(val)
    },
    {
        id: 'number',
        label: 'Number',
        dispalyLabel: '123',
        formatValue: (val, configs, placeholder) => {
            const num = toNum(val);
            if (!hasValue(num, true, true))
                return placeholder || val;
            if (!!configs && hasValue(configs === null || configs === void 0 ? void 0 : configs[0], true))
                return roundOff(num, toNum(configs === null || configs === void 0 ? void 0 : configs[0], 0));
            return num;
        },
        renderConfigs: (configs, onChange) => {
            return _jsx(_Fragment, { children: _jsxs(FormField, { children: [_jsx(Label, { children: "Decimal Places" }), _jsx(Input, { type: 'number', value: (configs === null || configs === void 0 ? void 0 : configs[0]) || '0', onChange: v => {
                                const parsed = toNum(v, undefined);
                                if (parsed == undefined)
                                    onChange(['0']);
                                if (parsed > 10)
                                    onChange(['10']);
                                if (parsed < 0)
                                    onChange(['0']);
                                else
                                    onChange([parsed.toString()]);
                            }, inputAttr: {
                                step: '1',
                                min: '0',
                                max: '10'
                            } })] }) });
        }
    },
    {
        id: 'boolean',
        label: 'True/False',
        dispalyLabel: 'T/F',
        formatValue: (val) => toBool(val)
    }
];
export function extractValueAndFormatters(expression) {
    const parts = expression.split('|').map(part => part.trim());
    const content = parts[0];
    const formatters = parts.slice(1);
    return { content, formatters };
}
const ExpressionRegexp = /{\$\.[^}]+}|{[^}]+\|[^}]*}/g;
/**
* @export
* Checks if a string contains placeholder expressions that can be parsed
*
* @example
* String with placeholders
* ```tsx
* hasPlaceholders("Hello {user.name}!") // Returns: true
* ```
*
* @example
* Plain string without placeholders
* ```tsx
* hasPlaceholders("Hello world!") // Returns: false
* ```
*/
export function hasPlaceholders(value) {
    const localRegexp = new RegExp(ExpressionRegexp);
    localRegexp.lastIndex = 0;
    return localRegexp.test(value);
}
function isPathValid(data, path) {
    try {
        const paths = JSONPath({ path, json: data, resultType: 'path' });
        return paths.length > 0;
    }
    catch (e) {
        return false;
    }
}
/**
* @export
* Applies formatting transformations to a value based on formatter configuration
* Uses FormatOptions to transform values (string, number, boolean, etc.)
*
* @example
* Format as number with decimals
* ```tsx
* applyFormatters(19.999, ["number", "2"]) // Returns: 19.99
* ```
*
* @example
* No formatters applied
* ```tsx
* applyFormatters("hello", []) // Returns: "hello"
* ```
*/
export function applyFormatters(value, formatters) {
    const formatterId = (!!formatters && hasValue(formatters === null || formatters === void 0 ? void 0 : formatters[0])) ? formatters[0] : null;
    if (!formatterId)
        return value;
    const formatOption = FormatOptions.find(f => f.id === formatterId);
    if (!formatOption)
        return value;
    return formatOption.formatValue(value, formatters.slice(1));
}
/**
* @export
* Extracts and formats a value from data using an expression with optional formatters
* Supports JSONPath expressions starting with $ and applies formatting options
*
* @example
* Extract nested property
* ```tsx
* extractAndFormatValue("$.user.name", { user: { name: "John" } }, "") // Returns: "John"
* ```
*
* @example
* Apply number formatting
* ```tsx
* extractAndFormatValue("$.price|number|2", { price: 19.999 }, "0") // Returns: 19.99
* ```
*/
export function extractAndFormatValue(expression, data, defaultValue) {
    const { content, formatters } = extractValueAndFormatters(expression);
    let rawValue = content;
    if (content === null || content === void 0 ? void 0 : content.startsWith('$')) {
        if (!isPathValid(data, content))
            return defaultValue;
        rawValue = JSONPath({ path: content, json: data, wrap: false });
    }
    return applyFormatters(rawValue, formatters);
}
/**
* @export
* Parses placeholder expressions in a string value and replaces them with actual data
* Returns the original type if single expression, otherwise returns formatted string
*
* @example
* Single expression returns typed value
* ```tsx
* parsePlaceholders("{user.age}", { user: { age: 25 } }) // Returns: 25 (number)
* ```
*
* @example
* Mixed content returns formatted string
* ```tsx
* parsePlaceholders("Hello {user.name}!", { user: { name: "John" } }) // Returns: "Hello John!"
* ```
*/
export function parsePlaceholders(value, data) {
    const strValue = String(value).trim();
    if (!hasValue(strValue))
        return value;
    if (!hasPlaceholders(strValue))
        return value;
    const localRegexp = new RegExp(ExpressionRegexp);
    localRegexp.lastIndex = 0;
    const matches = strValue.match(localRegexp);
    if (!matches || matches.length === 0)
        return value;
    // Single expression that spans the entire value
    if (matches.length === 1 && strValue === matches[0]) {
        const match = matches[0];
        const expression = extractExpressionFromMatch(match);
        return extractAndFormatValue(expression, data, '');
    }
    // Multiple expressions or mixed content - return string
    return strValue.replace(localRegexp, (match) => {
        const expression = extractExpressionFromMatch(match);
        const parsedValue = extractAndFormatValue(expression, data, '');
        return toStr(parsedValue);
    });
}
function extractExpressionFromMatch(match) {
    // remove {} from placeholders
    return match.slice(1, -1).trim();
}
/**
 * @export
 * Built-in expression syntaxes, so callers pass a name instead of a regex.
 *
 * Each matcher must capture the inner value in group 1 — `getExpressionAffixes()`
 * reads the surrounding wrapper back off it. `uxp-expression` is PillInput's
 * historical default matcher, so naming it changes no behaviour.
 */
export const ExpressionPresets = {
    'uxp-expression': { matcher: /{(.*?)}/, placeholder: '{$.param}' },
    'iviva-expression': { matcher: /#\{([^}]+)\}/, placeholder: '#{param}' },
};
const unescapeRegexLiteral = (s) => s.replace(/\\(.)/g, '$1');
// Must be non-empty: quantifiers such as [^}]+ reject an empty capture, which would
// make every such matcher fail the probe below and silently fall back.
const AFFIX_PROBE = '\u0001';
/**
 * @export
 * The literal prefix/suffix wrapping a pill's captured value, read off the matcher
 * itself so the syntax has a single source of truth.
 *
 * `/{(.*?)}/` → `{` `}` ・ `/#\{([^}]+)\}/` → `#{` `}` ・ `/\{\{(.*?)\}\}/` → `{{` `}}`
 *
 * Falls back to `{` `}` when the text around the capture group isn't a plain literal
 * (lookarounds, non-capturing groups, a `(` inside a character class). The fallback is
 * decided by round-tripping a probe through the matcher, not by inspecting the source —
 * corrupting a caller's expressions is far worse than ignoring an exotic matcher.
 */
export function getExpressionAffixes(matcher) {
    const FALLBACK = { prefix: '{', suffix: '}' };
    const src = matcher.source;
    const open = src.indexOf('(');
    const close = src.lastIndexOf(')');
    if (open < 0 || close <= open)
        return FALLBACK;
    const prefix = unescapeRegexLiteral(src.slice(0, open));
    const suffix = unescapeRegexLiteral(src.slice(close + 1));
    const probe = `${prefix}${AFFIX_PROBE}${suffix}`;
    const match = new RegExp(src).exec(probe);
    if (!match || match[0] !== probe || match[1] !== AFFIX_PROBE)
        return FALLBACK;
    return { prefix, suffix };
}
